import { Link } from "react-router-dom";
import { AlertTriangle, Circle, Square, MapPin, CalendarDays, ChevronRight } from "lucide-react";
import { fmtDate, TYPE_LABEL } from "@/lib/api";

const isRecent = (iso) => {
  const d = new Date(iso).getTime();
  return Date.now() - d < 1000 * 60 * 60 * 24 * 45;
};

export const StatusPill = ({ recall }) => {
  const cfg = recall.is_update
    ? { cls: "bg-amber-50 text-amber-700 border-amber-200", Icon: Circle, text: "Updated recall" }
    : isRecent(recall.published_at)
      ? { cls: "bg-red-50 text-red-700 border-red-200", Icon: AlertTriangle, text: "Active recall" }
      : { cls: "bg-slate-50 text-slate-600 border-slate-200", Icon: Square, text: "Historical" };
  return (
    <span
      data-testid="recall-status-pill"
      className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold uppercase tracking-wide ${cfg.cls}`}
    >
      <cfg.Icon className="h-3 w-3" /> {cfg.text}
    </span>
  );
};

export const RecallCard = ({ recall }) => (
  <Link
    to={`/recall/${recall.id}`}
    data-testid={`recall-card-${recall.id}`}
    className="group block overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_1px_3px_rgba(15,23,42,0.04)] transition-all duration-300 hover:-translate-y-1 hover:border-slate-300 hover:shadow-xl"
  >
    {recall.image_url && (
      <div className="h-44 w-full overflow-hidden bg-slate-100">
        <img src={recall.image_url} alt={`${recall.product_name || "Recalled product"} - Australian food recall`} className="h-full w-full object-cover" />
      </div>
    )}
    <div className="space-y-3.5 p-6">
      <div className="flex flex-wrap items-center gap-2">
        <StatusPill recall={recall} />
        <span className="rounded-full bg-slate-100 px-3 py-1 text-xs font-medium text-slate-600">
          {TYPE_LABEL[recall.recall_type] || "Recall"}
        </span>
      </div>
      <div>
        <p className="label-caps">Product</p>
        <h3 className="mt-1 font-display text-lg font-semibold leading-snug tracking-tight text-slate-900" data-testid="recall-card-product">
          {recall.product_name} {recall.product_size && `- ${recall.product_size}`}
        </h3>
      </div>
      <div>
        <p className="label-caps">Alert</p>
        <p className="mt-1 font-medium text-red-700" data-testid="recall-card-reason">{recall.short_reason}</p>
      </div>
      <div className="flex flex-wrap gap-5 pt-1 text-sm text-slate-600">
        <span className="inline-flex items-center gap-1.5" data-testid="recall-card-location">
          <MapPin className="h-4 w-4 text-slate-400" /> {recall.location_label}
        </span>
        <span className="inline-flex items-center gap-1.5">
          <CalendarDays className="h-4 w-4 text-slate-400" /> {fmtDate(recall.published_at)}
        </span>
      </div>
      <div className="flex items-center justify-between border-t border-slate-100 pt-3">
        <span className="text-xs text-slate-500">Source: Food Standards Australia New Zealand</span>
        <span className="inline-flex items-center gap-1 text-sm font-semibold text-slate-900 group-hover:gap-2 transition-all">
          View recall <ChevronRight className="h-4 w-4" />
        </span>
      </div>
    </div>
  </Link>
);
